/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Unread AccessNotices — the recipient's own record that their gym access
 * changed. Written only by the grant path; the client reads them and marks
 * them read.
 */

import { createSlice } from "@reduxjs/toolkit";
import { resolveBoxAccess } from "@lemonadetraining/contracts";

/** Bounds the in-memory list; matches the localStorage dismissal cap. */
const ACKNOWLEDGED_LIMIT = 20;

/**
 * Sticky by design, and it mirrors the mobile gate. `boxGrantVersion != null`
 * is what keeps a just-removed coach subscribed: their roles are gone by the
 * time the notice explaining that arrives, so a staff-only gate would unmount
 * the listener before it could ever deliver one.
 *
 * @param {Object} [claims] Redux claims copy.
 * @param {Object} [userInfoData] The Users document copy.
 * @return {boolean} Whether to subscribe.
 */
export const shouldMountAccessNotices = (claims, userInfoData) =>
  resolveBoxAccess(claims).isStaff ||
  userInfoData?.hasPTSubscription === true ||
  userInfoData?.boxGrantVersion != null;

const INIT = {
  unread: [],
  listenerFailed: false,
  acknowledgedIDs: [],
};

const withoutAcknowledged = (notices, acknowledgedIDs) =>
  notices.filter((notice) => !acknowledgedIDs.includes(notice.id));

const accessNoticesSlice = createSlice({
  name: "accessNotices",
  initialState: INIT,
  reducers: {
    setUnread(state, action) {
      state.unread = withoutAcknowledged(action.payload, state.acknowledgedIDs);
      state.listenerFailed = false;
    },
    setListenerFailed(state, action) {
      state.listenerFailed = action.payload === true;
    },
    // Local truth, held because the readAt write is a round trip: without it a
    // dismissed banner returns on the snapshot that has not caught up yet.
    acknowledge(state, action) {
      const id = action.payload;
      if (!state.acknowledgedIDs.includes(id)) {
        state.acknowledgedIDs.push(id);
        if (state.acknowledgedIDs.length > ACKNOWLEDGED_LIMIT) {
          state.acknowledgedIDs = state.acknowledgedIDs.slice(-ACKNOWLEDGED_LIMIT);
        }
      }
      state.unread = withoutAcknowledged(state.unread, state.acknowledgedIDs);
    },
    reset() {
      return INIT;
    },
  },
});

export const accessNoticesActions = accessNoticesSlice.actions;
export default accessNoticesSlice;
